// AXEL MACRO BRIEF — AI-powered fundamental analysis panel.
// Sits BELOW the four deterministic Market Intel cards (Fear/Greed, XAU
// Pivots, DXY, Economic Calendar). Two tabs: Gold (XAUUSD) and Bitcoin.
//
// Data comes from /api/market/macro-brief which is a thin cache reader —
// the actual Claude call + news fetch happens on a 30-min background
// warmer, so every user gets instant loads with zero per-request LLM cost.

import React, { useCallback, useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() {
  const token = localStorage.getItem("flexx_access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const T = {
  bg: "#060606", card: "#0d1218", border: "#1C1C1C",
  gold: "#C9A227", text: "#EDE8DC", mute: "#AAAAAA",
  green: "#22C55E", red: "#FF4444", yellow: "#FFB347", orange: "#FF8C42",
};

const ASSETS = [
  { id: "gold", label: "GOLD", icon: "🥇" },
  { id: "btc",  label: "BTC",  icon: "₿" },
];

export default function AxelMacroBrief() {
  const [activeTab, setActiveTab] = useState("gold");
  const [data, setData] = useState({ gold: null, btc: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    try {
      const url = `${API}/market/macro-brief${forceRefresh ? "?refresh=true" : ""}`;
      const res = await fetch(url, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({ gold: json.gold || null, btc: json.btc || null });
      setLastFetchedAt(new Date());
    } catch (e) {
      // Keep previous data on transient failures — don't clear cached brief
      console.warn("[AxelMacroBrief] fetch error:", e?.message || e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(false), 30 * 60 * 1000); // 30-min auto-refresh
    return () => clearInterval(t);
  }, [load]);

  const current = data[activeTab];
  const isWarming = current && current.warming_up;
  const brief = current && current.brief;

  return (
    <div data-testid="axel-macro-brief" style={{ marginTop: 32 }}>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", marginBottom: 14,
      }}>
        <div>
          <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 11, letterSpacing: 2.5, marginBottom: 4 }}>
            🧠 AXEL MACRO BRIEF
          </div>
          <h2 style={{
            margin: "2px 0 0", fontSize: 22,
            fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5, color: T.text,
          }}>AI-Powered Fundamental Analysis</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {lastFetchedAt && (
            <div data-testid="macro-brief-updated" style={{
              color: T.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
            }}>
              Updated {fmtRelTime(lastFetchedAt)}
            </div>
          )}
          <button data-testid="macro-brief-refresh-btn" onClick={() => load(true)} disabled={refreshing || loading}
            style={{
              all: "unset", cursor: refreshing ? "wait" : "pointer",
              padding: "6px 12px", borderRadius: 6,
              border: `1px solid ${T.gold}55`, color: T.gold,
              fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, fontWeight: 700,
              background: `${T.gold}10`,
            }}>{refreshing ? "REFRESHING…" : "↻ REFRESH"}</button>
        </div>
      </div>

      {/* Education disclaimer (always visible — students will misread bias as a signal) */}
      <div data-testid="macro-brief-disclaimer" style={{
        marginBottom: 14, padding: "8px 12px", borderRadius: 6,
        background: "rgba(201,162,39,0.06)", border: `1px solid ${T.gold}30`,
        color: T.gold, fontSize: 11, lineHeight: 1.45, fontFamily: "monospace", letterSpacing: 0.4,
      }}>
        ⚠ Education only · AXEL bias is sentiment analysis, NOT entry/exit signals · Always confirm with your own SuPER checklist before any trade.
      </div>

      {/* Asset tabs */}
      <div role="tablist" style={{
        display: "flex", gap: 0,
        borderBottom: `1px solid ${T.border}`, marginBottom: 14,
      }}>
        {ASSETS.map((a) => {
          const isActive = activeTab === a.id;
          return (
            <button
              key={a.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`macro-brief-tab-${a.id}`}
              onClick={() => setActiveTab(a.id)}
              style={{
                all: "unset", cursor: "pointer",
                padding: "10px 18px",
                fontFamily: "monospace", fontSize: 12, fontWeight: 800,
                letterSpacing: 1.5,
                color: isActive ? T.gold : T.mute,
                borderBottom: isActive ? `2px solid ${T.gold}` : "2px solid transparent",
                marginBottom: -1,
              }}>
              {a.icon} {a.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && !brief ? (
        <SkeletonState />
      ) : isWarming || !brief ? (
        <WarmingState onRetry={() => load(true)} />
      ) : (
        <>
          {activeTab === "btc" && <BtcFundamentalsPanel />}
          <BriefBody brief={brief} ctx={current.context_snapshot} session={current.market_session}
            headlines={current.news_headlines} polymarket={current.polymarket_signals} />
        </>
      )}

      {/* Bottom-right power badge */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <div style={{
          color: T.mute, fontFamily: "monospace", fontSize: 9, letterSpacing: 1.2,
          padding: "3px 8px", borderRadius: 4,
          border: `1px solid ${T.border}`, background: "rgba(201,162,39,0.04)",
        }}>
          ⚡ POWERED BY AXEL AI
        </div>
      </div>
    </div>
  );
}

// ── BriefBody — the 6 content blocks ─────────────────────────────────

function BriefBody({ brief, ctx, session, headlines, polymarket }) {
  const biasTone = brief.bias === "Bullish" ? T.green : brief.bias === "Bearish" ? T.red : T.yellow;
  const biasArrow = brief.bias === "Bullish" ? "▲" : brief.bias === "Bearish" ? "▼" : "—";

  return (
    <div data-testid="macro-brief-body" style={{ display: "grid", gap: 14 }}>
      {/* BLOCK 1 — Bias indicator */}
      <div data-testid="block-bias" style={{
        background: T.card, border: `1px solid ${biasTone}55`, borderLeft: `3px solid ${biasTone}`,
        borderRadius: 12, padding: 18, textAlign: "center",
      }}>
        <div style={{ fontFamily: "monospace", color: T.mute, fontSize: 10, letterSpacing: 2 }}>
          {session ? `${session.toUpperCase()} SESSION · ` : ""}BIAS
        </div>
        <div data-testid="bias-label" style={{
          fontSize: 32, fontWeight: 900, color: biasTone, letterSpacing: 2,
          fontFamily: "'Bebas Neue','Oswald',sans-serif", marginTop: 6,
        }}>
          {brief.bias.toUpperCase()} {biasArrow}
        </div>
        <StrengthBar value={brief.bias_strength} tone={biasTone} />
        <div data-testid="bias-confidence" style={{
          marginTop: 8, fontFamily: "monospace", fontSize: 10, color: T.mute, letterSpacing: 1.5,
        }}>
          STRENGTH {brief.bias_strength}/10 · CONFIDENCE {(brief.confidence || "medium").toUpperCase()}
        </div>
      </div>

      {/* BLOCK 2 — Key driver */}
      <div data-testid="block-key-driver" style={{
        background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.gold}`,
        borderRadius: 12, padding: 14,
      }}>
        <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>
          KEY DRIVER TODAY
        </div>
        <div style={{ color: T.gold, fontSize: 15, fontWeight: 700, lineHeight: 1.45 }}>
          {brief.key_driver || "—"}
        </div>
      </div>

      {/* BLOCK 3 — Fundamental factors (responsive grid) */}
      <div data-testid="block-factors" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      }}>
        {(brief.fundamental_factors || []).map((f, i) => {
          const tone = f.impact === "bullish" ? T.green : f.impact === "bearish" ? T.red : T.yellow;
          return (
            <div key={i} data-testid={`factor-card-${i}`} style={{
              background: T.card, border: `1px solid ${T.border}`,
              borderTop: `2px solid ${tone}`,
              borderRadius: 10, padding: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ flex: 1, color: T.text, fontSize: 12, fontWeight: 800 }}>{f.factor}</span>
                <span style={{
                  background: `${tone}18`, color: tone,
                  padding: "2px 8px", borderRadius: 4,
                  fontFamily: "monospace", fontSize: 9, letterSpacing: 1, fontWeight: 800,
                  border: `1px solid ${tone}40`,
                }}>
                  {(f.impact || "neutral").toUpperCase()}
                </span>
              </div>
              <div style={{ color: T.mute, fontSize: 11, lineHeight: 1.5 }}>{f.detail}</div>
            </div>
          );
        })}
      </div>

      {/* BLOCK 4 — News sentiment bar */}
      <div data-testid="block-sentiment" style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: 14,
      }}>
        <div style={{
          fontFamily: "monospace", color: T.mute, fontSize: 10, letterSpacing: 2, marginBottom: 10,
        }}>
          NEWS SENTIMENT SCORE
        </div>
        <SentimentBar value={brief.news_sentiment} />
        <div style={{
          display: "flex", justifyContent: "space-between",
          marginTop: 6, fontFamily: "monospace", fontSize: 9, color: T.mute, letterSpacing: 1,
        }}>
          <span>BEARISH −10</span>
          <span>NEUTRAL 0</span>
          <span>+10 BULLISH</span>
        </div>
      </div>

      {/* BLOCK 5 — Risk events */}
      <div data-testid="block-risk" style={{
        background: "rgba(255,140,66,0.06)", border: `1px solid ${T.orange}55`,
        borderLeft: `3px solid ${T.orange}`, borderRadius: 10, padding: 12,
      }}>
        <div style={{
          fontFamily: "monospace", color: T.orange, fontSize: 10, letterSpacing: 2, marginBottom: 4,
        }}>
          ⚠ RISK EVENTS AHEAD
        </div>
        <div style={{ color: T.text, fontSize: 12, lineHeight: 1.5 }}>
          {brief.risk_events || "No major catalysts flagged."}
        </div>
      </div>

      {/* BLOCK 6 — AXEL's Take (chat-bubble style) */}
      <div data-testid="block-axel-take" style={{
        background: "linear-gradient(135deg, #0d0a04 0%, #15110a 100%)",
        border: `1px solid ${T.gold}50`, borderLeft: `3px solid ${T.gold}`,
        borderRadius: 12, padding: 14, boxShadow: `0 0 18px rgba(201,162,39,0.08)`,
      }}>
        <div style={{
          fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2, marginBottom: 6,
        }}>
          💬 AXEL'S TAKE
        </div>
        <div style={{ color: "#FFFFFF", fontSize: 14, lineHeight: 1.6, fontStyle: "italic" }}>
          "{brief.axel_take || "Insufficient signal — stand down and wait for clarity."}"
        </div>
      </div>

      {/* 📰 Headlines — collapsible. Hidden when news_headlines is empty
          (e.g. all providers rate-limited). Show up to 3 most recent. */}
      {Array.isArray(headlines) && headlines.length > 0 && (
        <details data-testid="block-headlines" style={{
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px",
        }}>
          <summary style={{
            cursor: "pointer", fontFamily: "monospace", color: T.gold,
            fontSize: 10, letterSpacing: 1.5,
          }}>
            📰 HEADLINES INFORMING THIS BRIEF ({Math.min(3, headlines.length)})
          </summary>
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {headlines.slice(0, 3).map((h, i) => (
              <a key={h.url || `${h.title}-${i}`} href={h.url || "#"} target="_blank" rel="noopener noreferrer"
                style={{ color: T.text, fontSize: 12, lineHeight: 1.5, textDecoration: "none",
                  paddingLeft: 8, borderLeft: `2px solid ${T.gold}55`, display: "block" }}>
                <div style={{ marginBottom: 2 }}>{h.title}</div>
                <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace" }}>
                  {h.source || "—"}{h.published_at ? ` · ${new Date(h.published_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}` : ""}
                </div>
              </a>
            ))}
          </div>
        </details>
      )}

      {/* 🔮 Polymarket — collapsible. Always tries to fetch, hides when empty. */}
      {Array.isArray(polymarket) && polymarket.length > 0 && (
        <details data-testid="block-polymarket" style={{
          background: T.card, border: `1px solid #8b5cf655`, borderRadius: 8, padding: "10px 12px",
        }}>
          <summary style={{
            cursor: "pointer", fontFamily: "monospace", color: "#a78bfa",
            fontSize: 10, letterSpacing: 1.5,
          }}>
            🔮 POLYMARKET PREDICTIONS ({polymarket.length})
          </summary>
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {polymarket.map((p, i) => {
              const yp = p.yes_prob != null ? Math.round(p.yes_prob * 100) : null;
              return (
                <div key={`${p.question}-${i}`} style={{
                  paddingLeft: 8, borderLeft: `2px solid #8b5cf6`, color: T.text, fontSize: 12, lineHeight: 1.5,
                }}>
                  <div style={{ marginBottom: 2 }}>{p.question}</div>
                  <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace" }}>
                    {yp != null ? <span style={{ color: yp >= 50 ? T.green : T.red, fontWeight: 700 }}>{yp}% YES</span> : "no price"}
                    {p.volume ? <span style={{ marginLeft: 8 }}>· vol ${Math.round(Number(p.volume)).toLocaleString()}</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

function StrengthBar({ value, tone }) {
  const pct = Math.max(0, Math.min(10, Number(value) || 0)) * 10;
  return (
    <div data-testid="strength-bar" style={{
      marginTop: 12, height: 6, borderRadius: 3,
      background: "rgba(255,255,255,0.05)", border: `1px solid ${T.border}`,
      overflow: "hidden", maxWidth: 280, marginLeft: "auto", marginRight: "auto",
    }}>
      <div style={{
        width: `${pct}%`, height: "100%", background: tone,
        transition: "width 0.4s ease",
      }} />
    </div>
  );
}

function SentimentBar({ value }) {
  const v = Math.max(-10, Math.min(10, Number(value) || 0));
  const leftPct = ((v + 10) / 20) * 100;
  const tone = v >= 3 ? T.green : v <= -3 ? T.red : T.yellow;
  return (
    <div style={{ position: "relative", height: 22 }}>
      <div style={{
        position: "absolute", inset: 0,
        background: `linear-gradient(90deg, ${T.red}25 0%, ${T.red}25 35%, ${T.yellow}25 35%, ${T.yellow}25 65%, ${T.green}25 65%, ${T.green}25 100%)`,
        borderRadius: 6, border: `1px solid ${T.border}`,
      }} />
      <div data-testid="sentiment-marker" style={{
        position: "absolute", top: "50%", left: `${leftPct}%`,
        transform: "translate(-50%, -50%)",
        width: 18, height: 18, borderRadius: 9,
        background: tone, border: "2px solid #0d1218",
        boxShadow: `0 0 8px ${tone}80`,
      }} />
      <div data-testid="sentiment-value" style={{
        position: "absolute", left: `${leftPct}%`, top: "calc(100% + 4px)",
        transform: "translateX(-50%)",
        fontFamily: "monospace", color: tone, fontSize: 10, fontWeight: 800,
      }}>
        {v > 0 ? "+" : ""}{v}
      </div>
    </div>
  );
}

// ── Loading & warming states ─────────────────────────────────────────

function SkeletonState() {
  return (
    <div data-testid="macro-brief-skeleton" style={{ display: "grid", gap: 14 }}>
      {[80, 60, 100, 50, 70, 90].map((h, i) => (
        <div key={i} style={{
          height: h, borderRadius: 10,
          background: "linear-gradient(90deg, #0d1218 0%, #1a2230 50%, #0d1218 100%)",
          backgroundSize: "200% 100%",
          animation: "macroBriefShimmer 1.4s ease-in-out infinite",
        }} />
      ))}
      <style>{`
        @keyframes macroBriefShimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
      `}</style>
    </div>
  );
}

function WarmingState({ onRetry }) {
  return (
    <div data-testid="macro-brief-warming" style={{
      background: T.card, border: `1px dashed ${T.gold}55`,
      borderRadius: 12, padding: 28, textAlign: "center",
    }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🔎</div>
      <div style={{ color: T.text, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
        AXEL is analyzing the markets…
      </div>
      <div style={{ color: T.mute, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
        Brief refreshes every 30 minutes. Check back shortly.
      </div>
      <button onClick={onRetry} data-testid="macro-brief-warming-retry" style={{
        all: "unset", cursor: "pointer",
        padding: "8px 18px", borderRadius: 6,
        border: `1px solid ${T.gold}`, color: T.gold,
        fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
        background: `${T.gold}10`,
      }}>↻ TRY AGAIN</button>
    </div>
  );
}

function fmtRelTime(date) {
  if (!date) return "";
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

// ── BTC FUNDAMENTALS MINI-PANEL ──────────────────────────────────────
// Collapsible 4-signal snapshot (ETF flows · Funding rate · Stablecoin
// liquidity · BTC dominance) with a 0–100 composite score. Reads from
// /api/market/btc-fundamentals (10-min server cache, free upstream feeds).

const IMPACT_TONE = { bullish: "#22C55E", bearish: "#FF4444", neutral: "#FFB347" };

function BtcFundamentalsPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    try {
      const url = `${API}/market/btc-fundamentals${forceRefresh ? "?refresh=true" : ""}`;
      const res = await fetch(url, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e?.message || "Unable to load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const signals = (data && data.signals) || [];
  const score = data?.score ?? 0;
  const finalLabel = data?.label || "—";
  const scoreTone =
    score >= 56 ? T.green :     // Risk-On BTC
    score >= 46 ? T.yellow :    // Mixed BTC
    T.red;                       // Risk-Off BTC

  return (
    <div data-testid="btc-fundamentals-panel" style={{
      background: T.card,
      border: `1px solid ${T.gold}55`,
      borderLeft: `3px solid ${T.gold}`,
      borderRadius: 12, padding: 14, marginBottom: 14,
      boxShadow: `0 0 14px rgba(201,162,39,0.05)`,
    }}>
      {/* Header — clickable to toggle */}
      <button
        type="button"
        data-testid="btc-fundamentals-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          all: "unset", cursor: "pointer", width: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{
            fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2,
          }}>
            ₿ BTC FUNDAMENTALS
          </span>
          {data && (
            <span data-testid="btc-fundamentals-quickscore" style={{
              fontFamily: "monospace", color: scoreTone, fontSize: 10, letterSpacing: 1.5,
              fontWeight: 800,
              padding: "2px 8px", borderRadius: 4,
              background: `${scoreTone}18`, border: `1px solid ${scoreTone}40`,
            }}>
              SCORE {score} · {finalLabel.toUpperCase()}
            </span>
          )}
        </div>
        <span style={{
          color: T.mute, fontFamily: "monospace", fontSize: 11, fontWeight: 800,
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease", display: "inline-block",
        }}>▸</span>
      </button>

      {/* Body */}
      {open && (
        <div data-testid="btc-fundamentals-body" style={{ marginTop: 14 }}>
          {loading && !data ? (
            <div style={{ color: T.mute, fontSize: 12, padding: "10px 0" }}>Loading fundamentals…</div>
          ) : error && !data ? (
            <div style={{ color: T.red, fontSize: 12, padding: "10px 0" }}>
              {error}. <button data-testid="btc-fundamentals-retry" onClick={() => load(true)}
                style={{ all: "unset", cursor: "pointer", color: T.gold, textDecoration: "underline" }}>retry</button>
            </div>
          ) : (
            <>
              {/* 4 signal rows */}
              <div style={{ display: "grid", gap: 8 }}>
                {signals.map((s) => {
                  const tone = IMPACT_TONE[s.impact] || T.yellow;
                  const dim = s.available === false;
                  return (
                    <div key={s.label}
                      data-testid={`btc-fund-row-${(s.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      style={{
                        display: "grid", gridTemplateColumns: "1fr auto auto",
                        alignItems: "center", gap: 10,
                        padding: "8px 10px", borderRadius: 8,
                        background: "rgba(255,255,255,0.02)",
                        border: `1px solid ${T.border}`,
                        opacity: dim ? 0.6 : 1,
                      }}>
                      <div>
                        <div style={{ color: T.text, fontSize: 12, fontWeight: 800 }}>
                          {s.label}
                        </div>
                        {s.detail && (
                          <div style={{ color: T.mute, fontSize: 10, lineHeight: 1.4, marginTop: 2 }}>
                            {s.detail}
                          </div>
                        )}
                      </div>
                      <div style={{
                        color: dim ? T.mute : T.text,
                        fontFamily: "monospace", fontSize: 13, fontWeight: 800,
                        letterSpacing: 0.5, minWidth: 70, textAlign: "right",
                      }}>
                        {s.value || "—"}
                      </div>
                      <div style={{
                        background: `${tone}18`, color: tone,
                        padding: "3px 9px", borderRadius: 4,
                        fontFamily: "monospace", fontSize: 9, letterSpacing: 1, fontWeight: 800,
                        border: `1px solid ${tone}40`,
                        whiteSpace: "nowrap",
                      }}>
                        {(s.impact || "neutral").toUpperCase()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Score footer — gradient bar + needle + final label */}
              <div style={{
                marginTop: 14, padding: 12, borderRadius: 10,
                background: "linear-gradient(135deg, #0a0c11 0%, #11150d 100%)",
                border: `1px solid ${T.border}`,
              }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 8,
                }}>
                  <span style={{ fontFamily: "monospace", color: T.mute, fontSize: 10, letterSpacing: 2 }}>
                    COMPOSITE SCORE
                  </span>
                  <span data-testid="btc-fund-score-value" style={{
                    fontFamily: "'Bebas Neue','Oswald',sans-serif", color: scoreTone,
                    fontSize: 22, fontWeight: 900, letterSpacing: 1,
                  }}>
                    {score}/100
                  </span>
                </div>
                <ScoreGradientBar score={score} />
                <div data-testid="btc-fund-final-label" style={{
                  marginTop: 10, textAlign: "center",
                  background: `${scoreTone}14`, color: scoreTone,
                  border: `1px solid ${scoreTone}40`,
                  padding: "6px 10px", borderRadius: 6,
                  fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
                }}>
                  {finalLabel.toUpperCase()}
                </div>
              </div>

              {/* Footer — refresh + timestamp */}
              <div style={{
                marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center",
                fontFamily: "monospace", fontSize: 9, color: T.mute, letterSpacing: 1,
              }}>
                <span>
                  {data?.fetched_at ? `Updated ${new Date(data.fetched_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : ""}
                  {data?.cached ? " · cached" : ""}
                </span>
                <button data-testid="btc-fundamentals-refresh" onClick={() => load(true)} disabled={refreshing}
                  style={{
                    all: "unset", cursor: refreshing ? "wait" : "pointer",
                    padding: "4px 10px", borderRadius: 4,
                    border: `1px solid ${T.gold}40`, color: T.gold,
                    fontFamily: "monospace", fontSize: 9, letterSpacing: 1.5, fontWeight: 800,
                    background: `${T.gold}08`,
                  }}>
                  {refreshing ? "REFRESHING…" : "↻ REFRESH"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreGradientBar({ score }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div style={{ position: "relative", height: 14 }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: 7,
        background: "linear-gradient(90deg, #FF4444 0%, #FF8C42 25%, #FFB347 50%, #14B8A6 75%, #22C55E 100%)",
        border: `1px solid ${T.border}`,
      }} />
      <div data-testid="btc-fund-score-needle" style={{
        position: "absolute", top: -3, bottom: -3, left: `${pct}%`,
        width: 3, transform: "translateX(-50%)",
        background: "#FFFFFF", borderRadius: 2,
        boxShadow: "0 0 6px rgba(255,255,255,0.7)",
      }} />
    </div>
  );
}

